import React, { useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  type ChartOptions,
  type ChartData,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import ChartDeferred from 'chartjs-plugin-deferred';
import { Stack, Box } from '@mui/material';

import { TestScore } from '@common/@types/test_score';
import { MapProgressHeader } from './MapProgressHeader';
import {
  getGradientBorderColor,
  externalTooltipHandler,
  transformTestScoreData,
  getMinMaxPercentile,
  getInitialTestScoreDataSet,
  xAxisTickConfig,
} from './utils';

const DATASET_INDEX = {
  LOW_RANK: 0,
  HIGH_RANK: 1,
  ALL_RANKS: 2,
} as const;

ChartJS.register(
  Filler,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ChartDeferred
);

type Props = {
  mapTestScore: TestScore[] | undefined;
};

export const MapKnowledgeGrowthChart = ({ mapTestScore }: Props) => {
  const [showFullDataSet, setShowFullDataSet] = useState(false);

  const numberOfTests = mapTestScore?.length ?? 0;

  const testScoreData = transformTestScoreData(mapTestScore);
  const { highestRank, lowestRank } = getMinMaxPercentile(mapTestScore);
  const initialTestScoreData = getInitialTestScoreDataSet(testScoreData);

  const testScoreDataSet = showFullDataSet ? testScoreData : initialTestScoreData;
  const xAxisMaxValue = testScoreData?.[testScoreData.length - 1]?.x || 3;

  // When mapTestScore has less then 1 item, show full graph
  useEffect(() => {
    if (numberOfTests <= 2) {
      setShowFullDataSet(true);
    }
  }, [numberOfTests]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    clip: false,
    layout: {
      padding: {
        top: 24,
        right: 24,
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: false,
    },
    font: {
      family: 'Montserrat,sans-serif',
    },
    events: showFullDataSet
      ? ['mousemove', 'mouseout', 'click', 'touchstart', 'touchmove']
      : ['click'],
    onClick: () => {
      if (numberOfTests <= 2) {
        return;
      }

      setShowFullDataSet((state) => !state);

      // hide tooltip on click
      const tooltipEl = document.getElementById('chartjs-tooltip');
      if (tooltipEl) {
        setTimeout(() => {
          tooltipEl.style.opacity = '0';
        }, 300);
      }
    },
    scales: {
      y: {
        display: true,
        type: 'linear',
        position: 'left',
        grid: {
          display: true,
          drawOnChartArea: false,
          drawTicks: true,
        },
        title: {
          display: true,
          color: '#0F1629',
          text: 'Percentile Rank',
          font: {
            size: 16,
            weight: 'bold',
          },
        },
        max: 100,
        ticks: {
          stepSize: 10,
          font: {
            size: 16,
          },
          callback: (value) => `${value}th`,
        },
      },
      x: {
        type: 'linear',
        position: 'bottom',
        grid: {
          z: 1,
        },
        min: 0,
        max: Math.round(xAxisMaxValue * 10) / 10,
        ticks: {
          ...xAxisTickConfig,
          callback(value) {
            return Number(value) % 0.5 !== 0 ? '' : value;
          },
        },
        title: {
          display: true,
          text: 'Years in Alpha',
          color: '#0F1629',
          font: {
            size: 16,
            weight: 'bold',
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      filler: {
        propagate: true,
      },
      tooltip: {
        enabled: false,
        position: 'nearest',
        intersect: false,
        external: externalTooltipHandler,
        filter: (context) => context.datasetIndex === DATASET_INDEX.ALL_RANKS,
        callbacks: {
          label: (context) => {
            const dataIndex = context.dataIndex;
            const data = testScoreData[dataIndex];

            if (!data) {
              return [];
            }

            const { test_date, percentile } = data;

            return [percentile, test_date];
          },
        },
      },
      datalabels: {
        borderWidth: 1,
        borderRadius: 50,
        padding: 14,
        color: '#0AA6D1',
        borderColor: '#0AA6D1',
        backgroundColor: '#FFF',
        font: {
          size: 16,
          weight: 700,
        },
        display: (context) => {
          const {
            datasetIndex,
            dataIndex,
            dataset: { data },
          } = context;
          if (datasetIndex === DATASET_INDEX.LOW_RANK && dataIndex === 0) {
            return true;
          }

          if (datasetIndex === DATASET_INDEX.HIGH_RANK && dataIndex === data.length - 1) {
            return true;
          }

          return false;
        },
      },
      deferred: {
        yOffset: '75%',
      },
    },
  };

  const data: ChartData<'line'> = {
    labels: testScoreData.map((item) => item.x),
    datasets: [
      {
        label: 'Low Rank',
        data:
          numberOfTests <= 1 ? [] : Array(testScoreData.length).fill(initialTestScoreData?.[0]?.y),
        borderColor: '#B820FF',
        borderDash: [5, 5], // Dashed line
        pointRadius: 0,
        pointHoverRadius: 0,
        datalabels: {
          borderColor: '#B720FF',
          color: '#B720FF',
        },
      },
      {
        label: 'High Rank',
        data:
          numberOfTests <= 1 ? [] : Array(testScoreData.length).fill(initialTestScoreData?.[1]?.y),
        borderColor: '#25B9E3',
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
      },
      {
        fill: 'origin',
        label: 'Percentile',
        data: testScoreDataSet,
        backgroundColor: '#EAF0FE',
        tension: 0.4,
        pointHitRadius: 0,
        borderWidth: 5,
        pointRadius: showFullDataSet ? 3 : 0,
        pointHoverBorderWidth: 12,
        pointBackgroundColor: 'transparent',
        borderColor: getGradientBorderColor,
      },
    ],
  };

  return (
    <Stack spacing={5}>
      <MapProgressHeader low={lowestRank} high={highestRank} />
      <Line options={options} data={data} />
      {/* Tooltip is appended dynamically, do not remove */}
      <Box id="tooltip-container" />
    </Stack>
  );
};
